// ── BCHNostr — Multi-identity (personal + business Pages) ─────────
//
// A person logs in with ONE Nostr identity (their personal account, via
// nsec / extension / bunker). On top of that they may own one or more
// *Business Pages* — each a genuinely separate Nostr identity with its own
// keypair, profile, posts, business listing, and (because the tip wallet is
// keyed per-pubkey) its own BCH tip wallet.
//
// Unlike the personal account — whose key may live in an extension or a
// remote signer we never see — a Business Page's key is GENERATED and held
// by the app (an nsec in localStorage). That is a deliberate product choice
// (one-tap "act as my café") with a real cost: it is a second class of
// private key in a threat model where XSS is catastrophic. Two mitigations
// follow from that and are enforced elsewhere:
//   1. the owner is shown the business nsec once, to back up (Settings-style
//      export), so a wiped browser never means a lost business;
//   2. nothing here ever logs, transmits, or embeds a WIF or nsec.
//
// SWITCHING identities is deliberately implemented as "install this
// identity's signer, set bchnostr:current-user, reload" — exactly what
// login already does. Every hook in the app already keys off
// `bchnostr:current-user` (publishing, the per-pubkey tip wallet, DMs), so a
// reload re-bootstraps the whole app as the selected identity with no
// per-hook plumbing. This file owns the registry and the crypto; the switch
// itself lives in useCurrentUser.
//
// The link between an owner and its businesses is published to Nostr in BOTH
// directions so it is mutually verifiable (see the event helpers below).

import {
  generateSecretKey, getPublicKey, finalizeEvent, nip19,
  type Event, type EventTemplate,
} from 'nostr-tools';
import { getRelays } from '../hooks/useNostrPublish';

// ── Storage ───────────────────────────────────────────────────────

/** Registry of business Pages the current browser holds keys for. */
const REGISTRY_KEY = 'bchnostr:business-identities';
/** Per-business nsec. Mirrors the personal `bchnostr:nsec-key`. */
const BIZ_NSEC_PREFIX = 'bchnostr:biz-nsec:';

/** A business Page this browser can act as. */
export interface BusinessIdentity {
  /** The business's own pubkey (hex). */
  pubkey: string;
  /** The personal pubkey that owns / operates this Page (hex). */
  ownerPubkey: string;
  /** Cached display name for the switcher, so it renders before relays load. */
  name: string;
  /** Cached avatar URL for the switcher. */
  picture: string;
  /** When the Page was created in this browser (unix seconds). */
  createdAt: number;
}

function readRegistry(): BusinessIdentity[] {
  try {
    const raw = localStorage.getItem(REGISTRY_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isBusinessIdentity);
  } catch {
    return [];
  }
}

function isBusinessIdentity(v: unknown): v is BusinessIdentity {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.pubkey === 'string' && /^[0-9a-f]{64}$/i.test(o.pubkey) &&
    typeof o.ownerPubkey === 'string' && /^[0-9a-f]{64}$/i.test(o.ownerPubkey)
  );
}

function writeRegistry(list: BusinessIdentity[]): void {
  try {
    localStorage.setItem(REGISTRY_KEY, JSON.stringify(list));
  } catch {
    // Storage full/disabled — the feature degrades but never throws.
  }
}

/** All business Pages this browser holds keys for. */
export function listBusinessIdentities(): BusinessIdentity[] {
  return readRegistry();
}

/** Business Pages owned by a specific personal pubkey. */
export function businessesOwnedBy(ownerPubkey: string): BusinessIdentity[] {
  return readRegistry().filter(b => b.ownerPubkey === ownerPubkey);
}

/** Look up one business Page we hold, by its pubkey. */
export function getBusinessIdentity(pubkey: string): BusinessIdentity | null {
  return readRegistry().find(b => b.pubkey === pubkey) || null;
}

/** Do we hold the signing key for this pubkey (i.e. it's a business Page)? */
export function isOwnedBusiness(pubkey: string): boolean {
  return !!localStorage.getItem(BIZ_NSEC_PREFIX + pubkey);
}

// ── Business key material ─────────────────────────────────────────

/**
 * The secret key for a business Page, as raw bytes, or null if we don't
 * hold it. Callers use this ONLY to install a signer — it is never returned
 * to the UI except through the explicit one-time backup path.
 */
export function loadBusinessSecret(pubkey: string): Uint8Array | null {
  try {
    const nsec = localStorage.getItem(BIZ_NSEC_PREFIX + pubkey);
    if (!nsec) return null;
    const decoded = nip19.decode(nsec.trim());
    if (decoded.type !== 'nsec') return null;
    return decoded.data as Uint8Array;
  } catch {
    return null;
  }
}

/**
 * The business's nsec, for the one-time backup screen ONLY. Kept as a
 * distinct, obviously-named function so its (rare, sensitive) call sites
 * are easy to audit.
 */
export function exportBusinessNsec(pubkey: string): string | null {
  try {
    return localStorage.getItem(BIZ_NSEC_PREFIX + pubkey);
  } catch {
    return null;
  }
}

export interface CreatedBusiness {
  pubkey: string;
  npub: string;
  /** The nsec — shown to the owner ONCE, then only via exportBusinessNsec. */
  nsec: string;
  secretKey: Uint8Array;
}

/**
 * Mint a fresh business keypair, persist its nsec, and register it as owned
 * by `ownerPubkey`. Does NOT publish anything — the caller wires up the
 * kind 0 / 30499 / ownership-link events with the returned secret key.
 */
export function createBusinessIdentity(
  ownerPubkey: string,
  meta: { name: string; picture?: string },
): CreatedBusiness {
  const secretKey = generateSecretKey();
  const pubkey = getPublicKey(secretKey);
  const nsec = nip19.nsecEncode(secretKey);

  try {
    localStorage.setItem(BIZ_NSEC_PREFIX + pubkey, nsec);
  } catch {
    throw new Error('Could not save the business key — browser storage may be full or disabled.');
  }

  const entry: BusinessIdentity = {
    pubkey,
    ownerPubkey,
    name: meta.name.slice(0, 200),
    picture: meta.picture || '',
    createdAt: Math.floor(Date.now() / 1000),
  };
  const list = readRegistry().filter(b => b.pubkey !== pubkey);
  list.push(entry);
  writeRegistry(list);

  return { pubkey, npub: nip19.npubEncode(pubkey), nsec, secretKey };
}

/** Update the cached name/picture used by the switcher (best-effort). */
export function updateBusinessCache(
  pubkey: string, meta: { name?: string; picture?: string },
): void {
  const list = readRegistry();
  const entry = list.find(b => b.pubkey === pubkey);
  if (!entry) return;
  if (typeof meta.name === 'string') entry.name = meta.name.slice(0, 200);
  if (typeof meta.picture === 'string') entry.picture = meta.picture;
  writeRegistry(list);
}

/**
 * Forget a business Page in THIS browser: drop its key and registry entry.
 *
 * This does not (and can not) delete the Page from Nostr — the events remain
 * published. It only removes the ability to act as it from this device. The
 * caller should ensure the owner has backed up the nsec first.
 */
export function forgetBusinessIdentity(pubkey: string): void {
  try { localStorage.removeItem(BIZ_NSEC_PREFIX + pubkey); } catch {}
  writeRegistry(readRegistry().filter(b => b.pubkey !== pubkey));
}

// ── Ownership-link events ─────────────────────────────────────────
//
// The owner ⇄ business link is published in BOTH directions so either side
// proves it, and neither side can be forged by the other:
//
//   owner → businesses   kind 30078, d=bchnostr/businesses, t=bch-biz-owner
//                         one `p` tag per owned business pubkey. Signed by
//                         the owner — the authoritative list of Pages.
//
//   business → owner      kind 30078, d=bchnostr/operator, t=bch-biz-operator
//                         a single `p` tag naming the owner. Signed by the
//                         business — proves the Page accepts that operator.
//
// A link is MUTUALLY VERIFIED only when both events agree. A `p` tag is a
// signed claim, not a trust boundary: consumers that care about the link
// (e.g. "who runs this business?") must check both directions and filter by
// the expected author, exactly as with operator bans and other trust-
// sensitive queries.

export const OWNER_LIST_KIND = 30078;
export const OWNER_LIST_D_TAG = 'bchnostr/businesses';
export const OWNER_LIST_TAG = 'bch-biz-owner';

export const OPERATOR_KIND = 30078;
export const OPERATOR_D_TAG = 'bchnostr/operator';
export const OPERATOR_TAG = 'bch-biz-operator';

const HEX64 = /^[0-9a-f]{64}$/i;

/** Build the owner's list of business Pages (signed by the owner). */
export function buildOwnerListEvent(
  businessPubkeys: string[],
): { kind: number; tags: string[][]; content: string } {
  const unique = [...new Set(businessPubkeys.filter(pk => HEX64.test(pk)))].slice(0, 200);
  const tags: string[][] = [
    ['d', OWNER_LIST_D_TAG],
    ['t', OWNER_LIST_TAG],
    ['alt', 'Business Pages operated by this account on BCHNostr'],
    ...unique.map(pk => ['p', pk]),
  ];
  return { kind: OWNER_LIST_KIND, tags, content: JSON.stringify({ businesses: unique }) };
}

/** Build a business's pointer to its owner (signed by the business). */
export function buildOperatorEvent(
  ownerPubkey: string,
): { kind: number; tags: string[][]; content: string } {
  if (!HEX64.test(ownerPubkey)) throw new Error('Invalid owner pubkey');
  const tags: string[][] = [
    ['d', OPERATOR_D_TAG],
    ['t', OPERATOR_TAG],
    ['p', ownerPubkey],
    ['alt', 'This Business Page is operated by the named account on BCHNostr'],
  ];
  return { kind: OPERATOR_KIND, tags, content: JSON.stringify({ owner: ownerPubkey }) };
}

/** Read the business pubkeys out of an owner-list event (validated). */
export function parseOwnerList(ev: Event | null | undefined): string[] {
  if (!ev || ev.kind !== OWNER_LIST_KIND) return [];
  const dTag = ev.tags.find(t => t[0] === 'd')?.[1];
  if (dTag !== OWNER_LIST_D_TAG) return [];
  const fromTags = ev.tags
    .filter(t => t[0] === 'p' && typeof t[1] === 'string' && HEX64.test(t[1]))
    .map(t => t[1]);
  return [...new Set(fromTags)];
}

/** Read the owner pubkey out of a business's operator event (validated). */
export function parseOperator(ev: Event | null | undefined): string | null {
  if (!ev || ev.kind !== OPERATOR_KIND) return null;
  const dTag = ev.tags.find(t => t[0] === 'd')?.[1];
  if (dTag !== OPERATOR_D_TAG) return null;
  const owner = ev.tags.find(t => t[0] === 'p' && typeof t[1] === 'string' && HEX64.test(t[1]))?.[1];
  return owner || null;
}

// ── Publishing as a business ──────────────────────────────────────
//
// A Business Page's key is the freshly-generated app-held key, NOT the
// logged-in `window.nostr` (that is the owner). So its events are finalized
// locally with the business secret and published straight to the relays,
// mirroring the seeder's operator-key path. Never used for the owner's own
// events — those go through the normal useNostrPublish flow.

const PUBLISH_TIMEOUT_MS = 10000;

function publishSigned(signed: Event, relays: string[]): Promise<number> {
  const attempts = relays.map(url => new Promise<boolean>((resolve) => {
    let finished = false;
    const done = (accepted: boolean, ws?: WebSocket) => {
      if (finished) return;
      finished = true;
      try { ws?.close(); } catch {}
      resolve(accepted);
    };
    try {
      const ws = new WebSocket(url);
      ws.onopen = () => {
        try { ws.send(JSON.stringify(['EVENT', signed])); }
        catch { done(false, ws); }
      };
      ws.onmessage = (msgEv) => {
        try {
          const msg = JSON.parse(msgEv.data);
          if (Array.isArray(msg) && msg[0] === 'OK' && msg[1] === signed.id) {
            done(msg[2] === true, ws);
          }
        } catch {}
      };
      ws.onerror = () => done(false, ws);
      ws.onclose = () => done(false);
      setTimeout(() => done(false, ws), PUBLISH_TIMEOUT_MS);
    } catch {
      resolve(false);
    }
  }));

  return Promise.allSettled(attempts).then(settled =>
    settled.filter(s => s.status === 'fulfilled' && s.value).length,
  );
}

/**
 * Finalize an event template with a business's secret key and publish it to
 * the app's relays. Returns the number of relays that accepted it.
 */
export async function publishAsBusiness(
  secretKey: Uint8Array, template: EventTemplate,
): Promise<{ event: Event; accepted: number }> {
  const signed = finalizeEvent(template, secretKey);
  const accepted = await publishSigned(signed, getRelays());
  return { event: signed, accepted };
}
